/**
 * A collection of payment plans
 */
const PAYMENT_PLANS = {
  ANNUALLY: 'ANNUALLY',
  SEMI_ANNUALLY: 'SEMI_ANNUALLY',
  MONTHLY: 'MONTHLY',
  INSTALLMENT: 'INSTALLMENT'
};

/**
 * The number of payments in a year for each payment plan
 */
const PAYMENT_PLAN_FACTORS = {
  [PAYMENT_PLANS.ANNUALLY]: 1,
  [PAYMENT_PLANS.SEMI_ANNUALLY]: 2,
  [PAYMENT_PLANS.MONTHLY]: 12
};

/**
 * Schema for a premium object within bQuote.premiums
 */
const PREMIUM_FIELDS = {
  PAYMENT_PLAN: 'paymentPlan',
  AMOUNT: 'amount',
  DOWN_PAYMENT: 'downPayment',
  NUM_OF_PAYMENTS: 'numOfPayments', // For installment plans (total number of payments, including down payment)
  PAYMENT_METHOD: 'paymentMethod',
  ID: 'id'
};

const PAYMENT_METHODS = {
  CARD: 'CARD',
  ESCROW: 'ESCROW',
  ACH: 'ACH'
};

const FEE_FIELDS = {
  ACH: PAYMENT_METHODS.ACH,
  CARD: PAYMENT_METHODS.CARD,
  SERVICE_CHARGE: 'serviceCharge',
  INSTALLMENT_FEE: 'installmentFee',
  DEFAULT: 'DEFAULT'
};

const COVERAGE_FIELDS = {
  DWELLING: 'DWELLING',
  PERSONAL_PROPERTY: 'PERSONAL_PROPERTY',
  LIABILITY: 'LIABILITY',
  PER_PERSON_LIABILITY: 'PER_PERSON_LIABILITY',
  PER_ACCIDENT_LIABILITY: 'PER_ACCIDENT_LIABILITY',
  UNDERINSURED_PER_PERSON: 'UNDERINSURED_PER_PERSON',
  UNDERINSURED_PER_ACCIDENT: 'UNDERINSURED_PER_ACCIDENT',
  UNDERINSURED_PROPERTY_DAMAGE: 'UNDERINSURED_PROPERTY_DAMAGE',
  MEDICAL_PAYMENTS: 'MEDICAL_PAYMENTS',
  OTHER_STRUCTURES: 'OTHER_STRUCTURES',
  PROPERTY_DAMAGE_LIABILITY: 'PROPERTY_DAMAGE_LIABILITY',
  COMPREHENSIVE_DEDUCTIBLE: 'COMPREHENSIVE_DEDUCTIBLE',
  COLLISION_DEDUCTIBLE: 'COLLISION_DEDUCTIBLE'
};

const COVERAGE_LABELS = {
  [COVERAGE_FIELDS.DWELLING]: 'Dwelling',
  [COVERAGE_FIELDS.OTHER_STRUCTURES]: 'Other Structures',
  [COVERAGE_FIELDS.PERSONAL_PROPERTY]: 'Personal Property',
  [COVERAGE_FIELDS.LIABILITY]: 'Liability',
  [COVERAGE_FIELDS.MEDICAL_PAYMENTS]: 'Medical Payments',
  [COVERAGE_FIELDS.PER_PERSON_LIABILITY]: 'Per Person Liability',
  [COVERAGE_FIELDS.PER_ACCIDENT_LIABILITY]: 'Per Accident Liability',
  [COVERAGE_FIELDS.PROPERTY_DAMAGE_LIABILITY]: 'Property Damage Liability',
  [COVERAGE_FIELDS.COMPREHENSIVE_DEDUCTIBLE]: 'Comprehensive Deductible',
  [COVERAGE_FIELDS.COLLISION_DEDUCTIBLE]: 'Collision Deductible',
  [COVERAGE_FIELDS.UNDERINSURED_PER_PERSON]: 'Underinsured/Uninsured Per Person',
  [COVERAGE_FIELDS.UNDERINSURED_PER_ACCIDENT]: 'Underinsured/Uninsured Per Accident',
  [COVERAGE_FIELDS.UNDERINSURED_PROPERTY_DAMAGE]: 'Underinsured/Uninsured Property Damage'
};

const ADDITIONAL_COVERAGES = {
  LOSS_OF_USE: 'Loss of Use',
  THEFT_DEDUCTIBLE: 'Theft Deductible',
  REPLACEMENT_COST_CONTENTS: 'Replacement Cost Contents',
  INCREASED_REPLACEMENT_COST: 'Increased Replacement Cost',
  WATER_BACKUP: 'Water Backup',
  INCREASED_JEWELRY_LIMIT: 'Increased Jewelry Limit',
  LOSS_ASSESSMENT: 'Loss Assessment',
  COLLISION_DEDUCTIBLE: 'Collision Deductible',
  COMPREHENSIVE_DEDUCTIBLE: 'Comprehensive Deductible',
  TOWING_AND_LABOR: 'Towing & Labor',
  WIND_HURRICANE_DEDUCTIBLE: 'Wind / Hurricane Deductible',
  WINDSTORM_HAIL_DEDUCTIBLE: 'Windstorm / Hail Deductible',
  WIND_HAIL_HURRICANE_DEDUCTIBLE: 'Wind / Hail / Hurricane Deductible',
  HURRICANE_DEDUCTIBLE: 'Hurricane Deductible',
  NAMED_STORM_DEDUCTIBLE: 'Named Storm Deductible',
  UNINSURED_PROPERTY_DAMAGE: 'Uninsured Property Damage',
  UNINSURED_PROPERTY_DEDUCTIBLE: 'Uninsured Property Deductible',
  MEDICAL_PAYMENTS: 'Medical Payments',
  RENTAL_EXPENSE_PER_PERSON: 'Rental Expense per Person',
  RENTAL_EXPENSE_PER_ACCIDENT: 'Rental Expense per Accident',
  APIP: 'APIP',
  FIRST_PARTY_MEDICAL_BENEFITS: 'First Party Medical Benefits',
  TORT_LIMITATION: 'Tort Limitation',
  PERSONAL_INJURY: 'Personal Injury', // Not to be confused w/ PIP
  PIP_COVERAGE: 'PIP Coverage',
  PIP_LIMIT: 'PIP Limit',
  PIP_DEDUCTIBLE: 'PIP Deductible',
  PIP_APPLICATION: 'PIP Application',
  PIP_APIP: 'PIP/APIP',
  PIP_OPTIONS: 'PIP Options',
  WORK_LOSS: 'Work Loss',
  OPTIONAL_BASIC_ECONOMIC_LOSS: 'Optional Basic Economic Loss',
  STATE_SPONSORED_LIFE_INSURANCE: 'State Sponsored Life Insurance',
  EXTENDED_MEDICAL: 'Extended Medical',
  THRESHOLD: 'Threshold',
  OPTIONAL_BI_PER_PERSON: 'Optional BI per Person',
  OPTIONAL_BI_PER_ACCIDENT: 'Optional BI per Accident',
  FOUNDATION_COVERAGE: 'Foundation Coverage',
  RESIDENCE_GLASS: 'Residence Glass',
  MINE_SUBSIDENCE: 'Mine Subsidence',
  SINKHOLE: 'Sinkhole',
  PIP_PER_PERSON: 'PIP per Person',
  PIP_PER_ACCIDENT: 'PIP per Accident',
  APIP_PER_PERSON: 'APIP per Person',
  APIP_PER_ACCIDENT: 'APIP per Accident',
  PIP_APPLIES: 'PIP Applies',
  FULL_GLASS: 'Full Glass',
  OFF_PREMISES_THEFT_EXCLUSION: 'Off Premises Theft Exclusion',
  PERMITTED_INCIDENTAL_BUSINESS_OCCUPANCY: 'Permitted Incidental Business Occupancy',
  BASIC_REPARATION_BENEFITS: 'Basic Reparation Benefits',
  ROADSIDE_ASSISTANCE: 'Roadside Assistance',
  RENTAL_REIMBURSEMENT: 'Rental Reimbursement',
  UNINSURED_UNDERINSURED_MOTORIST: 'Uninsured/Underinsured Motorist Coverage',
  LOAN_LEASE_GAP: 'Loan/Lease Gap',
  UNINSURED_UNDERINSURED_STACKING: 'Uninsured/Underinsured Motorist Stacking'
};

const COVERAGE_KEYS = {
  TYPE: 'type',
  AMOUNT: 'amount'
};

const FINALIZATION_TYPES = {
  CAN_BEAT_PRICE: 'CAN_BEAT_PRICE',
  CANT_BEAT_PRICE: 'CANT_BEAT_PRICE',
  QUOTES_READY: 'QUOTES_READY'
};

const DISCOUNT_TYPES = {
  TELEMATICS: 'TELEMATICS'
};

module.exports = {
  PAYMENT_METHODS,
  FEE_FIELDS,
  PAYMENT_PLANS,
  PREMIUM_FIELDS,
  PAYMENT_PLAN_FACTORS,
  COVERAGE_FIELDS,
  ADDITIONAL_COVERAGES,
  COVERAGE_KEYS,
  COVERAGE_LABELS,
  FINALIZATION_TYPES,
  DISCOUNT_TYPES
};
